import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const ToolsList = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    function: "",
    status: true, // boolean
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // API response data
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchTools();
  }, [pagination.pageIndex, pagination.pageSize]);

  const fetchTools = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/tools", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });
      setTools(res.data || []);
      setPaginationInfo({
        total_count: res.pagination?.total_count || 0,
        total_pages: res.pagination?.total_pages || 0,
      });
    } catch {
      toast.error("Failed to fetch tools");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleCreate = async () => {
    if (!formData.name) return toast.error("Name required");

    setLoading(true);
    try {
      await apiClient.post("/tools", formData);
      handleCloseModal();
      fetchTools();
      toast.success("Tool created");
    } catch {
      toast.error("Create failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      function: tool.function,
      status: tool.status, // boolean
    });
    setOpenModal(true);
  };

  const handleUpdate = async () => {
    if (!editingTool) return;

    setLoading(true);
    try {
      await apiClient.put(`/tools/${editingTool._id}`, formData);
      handleCloseModal();
      fetchTools();
      toast.success("Tool updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (tool) => {
    const newStatus = !tool.status;

    try {
      await apiClient.put(`/tools/${tool._id}`, { status: newStatus });
      fetchTools();
    } catch {
      toast.error("Status update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tool?")) return;

    setLoading(true);
    try {
      await apiClient.delete(`/tools/${id}`);
      fetchTools();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingTool(null);
    setFormData({ name: "", function: "", status: true });
  };

  const columns = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "function", header: "Function" },

    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      Cell: ({ row }) => (
        <Switch
          checked={row.original.status === true}
          onChange={() => handleStatusToggle(row.original)}
          size="small"
        />
      ),
    },

    {
      accessorKey: "actions",
      header: "Actions",
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && tools.length === 0) return <PageLoader />;

  return (


    <>
    
    {/* Header */}
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <h3>Tools</h3>
      <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          New Tool
        </Button>
    </div>
    
    <Box sx={{ p: 3 }}>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search tools..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={tools.filter((t) =>
          t.name?.toLowerCase().includes(searchText.toLowerCase())
        )}
        manualPagination
        rowCount={paginationInfo.total_count}
        onPaginationChange={setPagination}
        state={{
          isLoading: loading,
          pagination: pagination,
        }}
        enableSorting
        enablePagination
        enableRowActions={false}
        enableColumnActions={false}
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
      />

      {/* Popup Form */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingTool ? "Edit Tool" : "Create Tool"}</DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            <TextField
              label="Tool Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Function"
              name="function"
              value={formData.function}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
            />

            {/* Status as TRUE/FALSE select for modal */}
            <TextField
              select
              label="Status"
              name="status"
              value={formData.status}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  status: e.target.value === "true", // convert to boolean
                }))
              }
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </TextField>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>

          <Button
            variant="contained"
            onClick={editingTool ? handleUpdate : handleCreate}
          >
            {editingTool ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    
    </>

  );
};

export default ToolsList;
